/**
 * @file IntegrationCard Component
 * @description Displays a clickable card for selecting cloud provider and integration module combinations.
 * Each card shows the cloud provider icon, integration type, and description.
 * Supports keyboard navigation and accessibility features.
 */

import CloudProviderIcon from 'components/IntegrationTypeIcon';
import Tooltip from 'components/Tooltip';
import React from 'react';
import { Container, Card } from 'reactstrap';

import {
  IntegrationCardProps,
  CLOUD_PROVIDER_CONFIG,
  INTEGRATION_MODULE_CONFIG,
} from './types';

/**
 * IntegrationCard - A memoized card component for cloud integration selection
 *
 * This component displays a clickable card that represents a specific combination
 * of cloud provider (e.g., AWS) and integration module (e.g., CSPM). When clicked,
 * it initiates the integration wizard flow.
 *
 * Features:
 * - Displays cloud provider icon and integration module information
 * - Shows tooltip with module description on hover
 * - Keyboard accessible (Enter/Space to activate)
 * - Memoized for performance optimization
 *
 * @component
 * @param {IntegrationCardProps} props - Component props
 * @param {CloudProvider} props.cloudProvider - The cloud provider (AWS, Azure, etc.)
 * @param {IntegrationModule} props.integrationModule - The integration module (CSPM, CIEM)
 * @param {Function} props.onClick - Callback when card is clicked
 * @returns {React.ReactElement} A clickable integration card with tooltip
 *
 * @example
 * <IntegrationCard
 *   cloudProvider={CloudProvider.AWS}
 *   integrationModule={IntegrationModule.CSPM}
 *   onClick={() => handleCardClick()}
 * />
 */
const IntegrationCard = React.memo<IntegrationCardProps>(
  ({ cloudProvider, integrationModule, onClick }) => {
    // Get configuration for the selected integration module
    const moduleConfig = INTEGRATION_MODULE_CONFIG[integrationModule];
    const title = moduleConfig.label;
    const fullName = moduleConfig.fullName;

    return (
      <Tooltip
        id={`bpa-account-integration-tooltip-${cloudProvider}-${integrationModule}`}
        title={title}
        tooltip={moduleConfig.description}
      >
        <Container
          className="d-flex justify-content-center align-items-center"
          style={{ width: '200px', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyPress={(e) => {
            // Support keyboard navigation: Enter or Space key triggers click
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }}
          aria-label={`Connect ${CLOUD_PROVIDER_CONFIG[cloudProvider].label} ${fullName}`}
        >
          <Card className="card-chart border border-light p-3 text-center hover-shadow">
            {/* Cloud provider icon */}
            <span className="align-self-center mb-3">
              <CloudProviderIcon cloudProviderType={cloudProvider} size={48} />
            </span>
            {/* Integration module name */}
            <h4 className="mb-0">{title}</h4>
            <small className="text-muted">{fullName}</small>
          </Card>
        </Container>
      </Tooltip>
    );
  },
);

IntegrationCard.displayName = 'IntegrationCard';

export default IntegrationCard;
